// --- Dependencies -------------------------------------------------------------------------------
import { useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useAuth from "@/hooks/useAuth";
import usePopup from "@/hooks/usePopup";
import useTranslation from "@/hooks/useTranslation";
import ProfilePopup from "@/UI/popups/ProfilePopup";
import QrCodesPopup from "@/UI/popups/QrCodesPopup";
import "./styles.css";

// --- Constants ----------------------------------------------------------------------------------

/** Opening the profile menu this many times inside the window below reveals the menu editor. */
const EDITOR_KNOCKS = 4;
const EDITOR_KNOCK_WINDOW_MS = 3000;

// --- Types --------------------------------------------------------------------------------------

type Props = {
    onMenuClick: () => void;
};

// --- Logic --------------------------------------------------------------------------------------

export default function AppBar({ onMenuClick }: Props) {
    const t = useTranslation();
    const client = useAuth((state) => state.client);
    const { addComp, removeComp } = usePopup();
    const { pathname } = useLocation();
    const navigate = useNavigate();
    const profileRef = useRef<HTMLButtonElement>(null);

    /** Timestamps of recent profile-menu opens, trimmed to the knock window on every open. */
    const knocksRef = useRef<number[]>([]);

    const ROUTE_LABELS: Record<string, string> = {
        "/": t.sideBar.home,
        "/menu": t.sideBar.menu,
        "/menu-editor": t.menuEditor.title,
        "/orders": t.sideBar.orders,
        "/branches": t.sideBar.branches,
        "/clients": t.sideBar.clients,
        "/subscriptions": t.sideBar.subscriptions,
        "/settings": t.sideBar.settings,
        "/support": t.sideBar.support,
        "/profile": t.sideBar.profile,
    };

    const pageTitle =
        Object.entries(ROUTE_LABELS)
            .filter(([key]) => pathname === key || (key !== "/" && pathname.startsWith(key + "/")))
            .sort((a, b) => b[0].length - a[0].length)[0]?.[1] ?? "";

    const openQrCodes = () => {
        const name = "qr-codes";
        removeComp(name);
        addComp({ name, Component: QrCodesPopup, place: "center", anim: "scale", cover: "shaded" });
    };

    const openProfileMenu = () => {
        const rect = profileRef.current?.getBoundingClientRect();
        if (!rect) return;
        const name = "profile-menu";
        removeComp(name);

        /**
         * The menu editor is unlisted — no button anywhere reaches it — but the installed PWA has no
         * address bar to type its URL into. Knocking on the profile menu is the way in for the
         * freelancers who use it, and it is obscure enough that nobody finds it by accident.
         */
        const now = Date.now();
        knocksRef.current = [...knocksRef.current, now].filter((stamp) => now - stamp < EDITOR_KNOCK_WINDOW_MS);
        if (knocksRef.current.length >= EDITOR_KNOCKS) {
            knocksRef.current = [];
            navigate("/menu-editor");
            return;
        }

        addComp({
            name,
            Component: ProfilePopup,
            place: "anchor-below",
            anim: "scaleTop",
            cover: "invisible",
            coverClose: true,
            anchor: { top: rect.top, left: rect.left, right: rect.right, bottom: rect.bottom, width: rect.width, height: rect.height },
        });
    };

    return (
        <header className="app-bar">
            <button type="button" className="app-bar-menu-btn" aria-label="Menu" onClick={onMenuClick}>
                <img src="/icons/menu-icon.svg" alt="" width={18} height={16} />
            </button>
            <p className="app-bar-title">{pageTitle}</p>
            <div className="app-bar-spacer" />
            <div className="app-bar-actions">
                <button type="button" className="app-bar-icon-btn" onClick={openQrCodes}>
                    <img src="/icons/qr-code-icon.svg" alt="" width={16} height={16} />
                </button>
                {client && (
                    <button ref={profileRef} type="button" className="app-bar-profile" onClick={openProfileMenu}>
                        <div className="app-bar-profile-avatar">
                            {client.picture ? (
                                <img src={client.picture} alt={client.name} />
                            ) : (
                                <img
                                    className="app-bar-profile-avatar-placeholder"
                                    src="/icons/client-small-placeholder-icon.svg"
                                    alt={client.name}
                                    width={20}
                                    height={20}
                                />
                            )}
                        </div>
                        <p className="app-bar-profile-name">{client.name}</p>
                    </button>
                )}
            </div>
        </header>
    );
}
